import { CheckCircle, Loader2, Package } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ProductThumbnail } from '../ui/aspect-ratio-image';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';

interface InventoryEligibilityRow {
  id: string;
  name: string;
  image_url: string | null;
  shopify_product_id: string | null;
  shopify_variant_id: string | null;
  supplier_stock: number;
  warehouse_stock: number;
  target_qty: number;
  eligible: boolean;
  reason: string | null;
  supercycle_inventory_synced_at: string | null;
  supercycle_inventory_last_target: number | null;
  supercycle_inventory_last_available: number | null;
}

interface InventoryPushResultRow {
  template_id: string;
  template_name?: string;
  status: 'ok' | 'skipped' | 'error';
  target?: number;
  before?: number;
  after?: number;
  created?: number;
  retired?: number;
  message?: string;
}

const ERROR_TOAST_MS = 8000;
const BETWEEN_PRODUCT_MS = 1000;
const MAX_RATE_RETRIES = 6;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const isRateLimitMsg = (msg?: string) => !!msg && /429|rate limit/i.test(msg);
const rateRetryWaitMs = (attempt: number) => Math.min(60_000, 2000 * 2 ** attempt);

export default function SupercycleInventoryTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<InventoryEligibilityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pushing, setPushing] = useState(false);
  const [pushProgress, setPushProgress] = useState<{
    done: number;
    total: number;
    ok: number;
    failed: number;
    currentName?: string;
    rateLimitWaitSec?: number;
  } | null>(null);
  const [rowPushing, setRowPushing] = useState<string | null>(null);
  const [results, setResults] = useState<Map<string, InventoryPushResultRow>>(new Map());

  const loadEligibility = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const t = localStorage.getItem('token');
      const r = await fetch('/api/supercycle/inventory-push/eligibility', {
        headers: { Authorization: `Bearer ${t}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to load eligibility');
      const list = Array.isArray(d.data) ? d.data : [];
      setRows(
        list.map((x: Record<string, unknown>) => ({
          id: String(x.id),
          name: String(x.name ?? ''),
          image_url: x.image_url != null ? String(x.image_url) : null,
          shopify_product_id: x.shopify_product_id != null ? String(x.shopify_product_id) : null,
          shopify_variant_id: x.shopify_variant_id != null ? String(x.shopify_variant_id) : null,
          supplier_stock: Number(x.supplier_stock) || 0,
          warehouse_stock: Number(x.warehouse_stock) || 0,
          target_qty: Number(x.target_qty) || 0,
          eligible: Boolean(x.eligible),
          reason: x.reason != null ? String(x.reason) : null,
          supercycle_inventory_synced_at:
            x.supercycle_inventory_synced_at != null ? String(x.supercycle_inventory_synced_at) : null,
          supercycle_inventory_last_target:
            x.supercycle_inventory_last_target != null ? Number(x.supercycle_inventory_last_target) : null,
          supercycle_inventory_last_available:
            x.supercycle_inventory_last_available != null ? Number(x.supercycle_inventory_last_available) : null,
        })),
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load inventory eligibility');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEligibility();
  }, [loadEligibility]);

  const eligibleIds = useMemo(() => rows.filter((r) => r.eligible).map((r) => r.id), [rows]);
  const allEligibleSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selected.has(id));

  const toggleAllEligible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allEligibleSelected) eligibleIds.forEach((id) => next.delete(id));
      else eligibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const pushOneResult = async (id: string, token: string): Promise<InventoryPushResultRow> => {
    const r = await fetch(`/api/asset-templates/${id}/supercycle/inventory/push`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.message || 'Sync failed');
    return d.data as InventoryPushResultRow;
  };

  const nameFor = (id: string) => rows.find((r) => r.id === id)?.name ?? id;

  const pushIds = async (ids: string[]) => {
    if (!ids.length) return;
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    let ok = 0;
    let failed = 0;

    for (let i = 0; i < ids.length; i++) {
      if (i > 0) await sleep(BETWEEN_PRODUCT_MS);
      const id = ids[i];
      let row: InventoryPushResultRow | null = null;

      for (let attempt = 0; attempt <= MAX_RATE_RETRIES; attempt++) {
        setPushProgress({ done: i, total: ids.length, ok, failed, currentName: nameFor(id) });
        try {
          row = await pushOneResult(id, token);
        } catch (e) {
          row = {
            template_id: id,
            status: 'error',
            message: e instanceof Error ? e.message : 'Sync failed',
          };
        }

        const rateLimited = row.status === 'error' && isRateLimitMsg(row.message);
        if (!rateLimited || attempt === MAX_RATE_RETRIES) break;

        const waitMs = rateRetryWaitMs(attempt);
        setPushProgress({
          done: i,
          total: ids.length,
          ok,
          failed,
          currentName: nameFor(id),
          rateLimitWaitSec: Math.ceil(waitMs / 1000),
        });
        await sleep(waitMs);
      }

      if (!row) continue;
      const finalRow = row;
      setResults((prev) => new Map(prev).set(id, finalRow));
      if (finalRow.status === 'ok') ok++;
      else if (finalRow.status === 'error') failed++;
      setPushProgress({ done: i + 1, total: ids.length, ok, failed, currentName: nameFor(id) });
    }

    if (failed) toast.error(`${failed} failed · ${ok} synced`, { duration: ERROR_TOAST_MS });
    else toast.success(`Synced inventory for ${ok} product(s)`);
  };

  const pushSelected = async () => {
    const ids = Array.from(selected).filter((id) => rows.find((r) => r.id === id)?.eligible);
    if (!ids.length) {
      toast.message('Select eligible products first');
      return;
    }
    setPushProgress({ done: 0, total: ids.length, ok: 0, failed: 0, currentName: nameFor(ids[0]) });
    setPushing(true);
    try {
      await pushIds(ids);
      await loadEligibility({ silent: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Sync failed', { duration: ERROR_TOAST_MS });
    } finally {
      setPushing(false);
      setPushProgress(null);
    }
  };

  const syncAllEligible = async () => {
    const ids = eligibleIds;
    if (!ids.length) {
      toast.message('No eligible products');
      return;
    }
    setSelected(new Set(ids));
    setPushProgress({ done: 0, total: ids.length, ok: 0, failed: 0, currentName: nameFor(ids[0]) });
    setPushing(true);
    try {
      await pushIds(ids);
      await loadEligibility({ silent: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Sync failed', { duration: ERROR_TOAST_MS });
    } finally {
      setPushing(false);
      setPushProgress(null);
    }
  };

  const pushOne = async (id: string) => {
    setRowPushing(id);
    try {
      const t = localStorage.getItem('token');
      if (!t) throw new Error('Not authenticated');
      const row = await pushOneResult(id, t);
      setResults((prev) => new Map(prev).set(id, row));
      if (row.status === 'ok') toast.success(row.message || 'Inventory synced');
      else toast.message(row.message || row.status);
      await loadEligibility({ silent: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setRowPushing(null);
    }
  };

  const pushPct =
    pushProgress && pushProgress.total > 0
      ? Math.min(100, Math.round((pushProgress.done / pushProgress.total) * 100))
      : 0;
  const pushBarValue = pushing ? Math.max(pushPct, pushPct === 100 ? 100 : 5) : pushPct;

  const fmtDate = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Supercycle inventory</CardTitle>
        <p className="text-sm text-muted-foreground pt-1 whitespace-nowrap overflow-x-auto">
          Reconciles Supercycle items to supplier + warehouse stock. Subscription push uses the Shopify <strong className="font-medium text-foreground">product</strong> id; inventory sync also needs the Shopify <strong className="font-medium text-foreground">variant</strong> id (auto-resolved from the product on refresh).
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {pushing && pushProgress ? (
          <div className="space-y-2 rounded-md border border-primary/20 bg-primary/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span className="font-medium">Syncing inventory to Supercycle</span>
              <span className="font-mono text-muted-foreground">
                {pushProgress.done} / {pushProgress.total} ({pushPct}%)
              </span>
            </div>
            <Progress value={pushBarValue} className="h-4" />
            <p className="text-xs text-muted-foreground">
              {pushProgress.rateLimitWaitSec != null ? (
                <span className="block text-amber-700 dark:text-amber-400">
                  Rate limited — waiting {pushProgress.rateLimitWaitSec}s before retrying
                  {pushProgress.currentName ? ` “${pushProgress.currentName}”` : ''}…
                </span>
              ) : pushProgress.currentName ? (
                <span className="block truncate">Current: {pushProgress.currentName}</span>
              ) : null}
              {pushProgress.ok} synced
              {pushProgress.failed > 0 ? (
                <span className="text-destructive"> · {pushProgress.failed} failed</span>
              ) : null}
            </p>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={pushing || loading} onClick={() => pushSelected()}>
            {pushing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Syncing…
              </>
            ) : (
              'Sync selected inventory'
            )}
          </Button>
          <Button type="button" variant="secondary" disabled={pushing || loading || !eligibleIds.length} onClick={() => syncAllEligible()}>
            {pushing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Syncing…
              </>
            ) : (
              'Sync all eligible'
            )}
          </Button>
          <Button type="button" variant="outline" disabled={loading || pushing} onClick={() => loadEligibility()}>
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-2 w-10">
                    <Checkbox
                      checked={allEligibleSelected}
                      onCheckedChange={() => toggleAllEligible()}
                      aria-label="Select all eligible"
                    />
                  </th>
                  <th className="text-left p-2 min-w-[12rem]">Product</th>
                  <th className="text-left p-2 min-w-[8rem]">Shopify product</th>
                  <th className="text-left p-2 min-w-[8rem]">Shopify variant</th>
                  <th className="text-left p-2">Supplier</th>
                  <th className="text-left p-2">Warehouse</th>
                  <th className="text-left p-2">Target</th>
                  <th className="text-left p-2 min-w-[8rem]">Last sync</th>
                  <th className="text-left p-2">SC available</th>
                  <th className="text-left p-2 min-w-[9rem]">Status</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const res = results.get(r.id);
                  return (
                    <tr key={r.id} className="border-t">
                      <td className="p-2">
                        <Checkbox
                          checked={selected.has(r.id)}
                          disabled={!r.eligible}
                          onCheckedChange={(c) => {
                            setSelected((prev) => {
                              const next = new Set(prev);
                              if (c) next.add(r.id);
                              else next.delete(r.id);
                              return next;
                            });
                          }}
                          aria-label={`Select ${r.name}`}
                        />
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-3 min-w-[12rem]">
                          <ProductThumbnail
                            src={r.image_url}
                            alt={r.name}
                            size="sm"
                            className="w-10 h-10"
                            fallback={<Package className="h-5 w-5 text-muted-foreground" />}
                          />
                          <button
                            type="button"
                            className="text-primary hover:underline text-left font-medium"
                            onClick={() => navigate(`/assets/${r.id}`)}
                          >
                            {r.name}
                          </button>
                        </div>
                      </td>
                      <td className="p-2 font-mono text-xs">{r.shopify_product_id || '—'}</td>
                      <td className="p-2 font-mono text-xs">{r.shopify_variant_id || '—'}</td>
                      <td className="p-2">{r.supplier_stock}</td>
                      <td className="p-2">{r.warehouse_stock}</td>
                      <td className="p-2">{r.target_qty}</td>
                      <td className="p-2 text-muted-foreground text-xs">{fmtDate(r.supercycle_inventory_synced_at)}</td>
                      <td className="p-2">{r.supercycle_inventory_last_available ?? '—'}</td>
                      <td className="p-2">
                        {res?.status === 'ok' ? (
                          <Badge variant="default" className="gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Synced
                          </Badge>
                        ) : res?.status === 'error' ? (
                          <Badge variant="destructive">Error</Badge>
                        ) : r.eligible ? (
                          <Badge variant="secondary">Ready</Badge>
                        ) : (
                          <Badge variant="outline">Skipped</Badge>
                        )}
                        {!r.eligible && r.reason ? (
                          <p className="text-xs text-muted-foreground mt-1 max-w-xs">{r.reason}</p>
                        ) : null}
                        {res?.message ? <p className="text-xs text-muted-foreground mt-1 max-w-xs">{res.message}</p> : null}
                      </td>
                      <td className="p-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={!r.eligible || rowPushing === r.id || pushing}
                          onClick={() => pushOne(r.id)}
                        >
                          {rowPushing === r.id ? 'Syncing…' : 'Sync'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
